import React, { useMemo } from 'react';
import { Eye, ChevronLeft, Database, MessageSquare, CheckCircle2, XCircle, Image, BookOpen, ShieldCheck, Tag } from 'lucide-react';
import { Badge } from '../../../../components/ui/Badge';
import { type Question, getQuestionWithExplanation } from '../../../../content';
import { QuestionCardClean } from '../../../../practice/components/QuestionCardClean';
import { QuestionRendererFactory } from '../../../../practice/components/renderers/QuestionRendererFactory';
import { VisualAidFactory } from '../../../../visuals/components/VisualAidFactory';
interface QuestionPreviewPanelProps {
  questionId: string | null;
  onBack?: () => void;
  accentColor?: 'blue' | 'purple' | 'emerald' | 'amber';
  showBackOnMobile?: boolean;
  onNavigateToGovernance?: (questionId: string) => void;
}
const ACCENT_COLORS = {
  blue: {
    bg: 'bg-blue-50',
    border: 'border-l-blue-500',
    text: 'text-blue-600'
  },
  purple: {
    bg: 'bg-purple-50',
    border: 'border-l-purple-500',
    text: 'text-purple-600'
  },
  emerald: {
    bg: 'bg-emerald-50',
    border: 'border-l-emerald-500',
    text: 'text-emerald-600'
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-l-amber-500',
    text: 'text-amber-600'
  }
};
export function QuestionPreviewPanel({
  questionId,
  onBack,
  accentColor = 'blue',
  showBackOnMobile = true,
  onNavigateToGovernance
}: QuestionPreviewPanelProps) {
  const question = useMemo(() => {
    if (!questionId) return null;
    return getQuestionWithExplanation(questionId);
  }, [questionId]);
  const colors = ACCENT_COLORS[accentColor];
  const visualCount = useMemo(() => {
    if (!question) return 0;
    return (question.stimulusAids?.length || 0) + (question.supportAids?.length || 0);
  }, [question]);
  // Tier color helper
  const getTierColor = (tier?: number): 'neutral' | 'success' | 'info' | 'warning' => {
    if (!tier) return 'neutral';
    if (tier <= 3) return 'success';
    if (tier <= 6) return 'info';
    return 'warning';
  };
  // Empty state
  if (!question) {
    return <div className="flex-1 flex items-center justify-center text-gray-400 p-6 bg-slate-50" data-id="element-5994">
        <div className="text-center" data-id="element-5995">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4" data-id="element-5996">
            <Eye className="w-8 h-8 text-gray-300" data-id="element-5997" />
          </div>
          <p className="font-semibold text-gray-500" data-id="element-5998">Selecteer een vraag</p>
          <p className="text-sm mt-1 text-gray-400" data-id="element-5999">
            Kies een vraag uit de lijst om de preview te bekijken
          </p>
        </div>
      </div>;
  }
  return <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50" style={{
    WebkitOverflowScrolling: 'touch'
  }} data-id="element-6000">
      <div className="p-4 sm:p-6 md:p-8 pb-8 sm:pb-12" data-id="element-6001">
        <div className="max-w-3xl mx-auto" data-id="element-6002">
          {/* Mobile back button */}
          {showBackOnMobile && onBack && <div className="mb-4 lg:hidden" data-id="element-6003">
              <button onClick={onBack} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors" data-id="element-6004">
                <ChevronLeft className="w-4 h-4" data-id="element-6005" />
                Terug naar lijst
              </button>
            </div>}

          {/* Question header badges */}
          <div className="mb-4 flex flex-wrap items-center gap-2" data-id="element-6006">
            <Badge variant="info" size="sm" data-id="element-6007">
              {question.questionType}
            </Badge>
            <span className="text-xs text-gray-500 font-mono truncate max-w-[200px]" data-id="element-6008">
              {question.familyId}
            </span>
            {visualCount > 0 && <div className="flex items-center gap-1.5 text-xs font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded-md border border-purple-100" data-id="element-6009">
                <Image className="w-3.5 h-3.5" data-id="element-6010" />
                {visualCount} visual{visualCount > 1 ? 's' : ''}
              </div>}
          </div>

          {/* Question Card */}
          <QuestionCardClean question={question} data-id="element-6011">
            <QuestionRendererFactory question={question} onAnswer={() => {}} data-id="element-6012" />
          </QuestionCardClean>

          {onNavigateToGovernance && <div className="mt-4 flex justify-end" data-id="element-6013">
              <button type="button" onClick={() => onNavigateToGovernance(question.id)} className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition-colors hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500" data-id="element-6014">
                <ShieldCheck className="w-4 h-4" data-id="element-6015" />
                Bekijk validatie
              </button>
            </div>}

          {/* Metadata & Feedback Section */}
          <div className="mt-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-6016">
            {/* Metadata Grid */}
            <div className="p-4 sm:p-5 border-b border-gray-100" data-id="element-6017">
              <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-6018">
                <Database className="w-4 h-4 text-gray-400" data-id="element-6019" />
                Vraag Metadata
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm" data-id="element-6020">
                <div data-id="element-6021">
                  <span className="block text-xs text-gray-500 mb-1" data-id="element-6022">
                    Skill
                  </span>
                  <span className="font-medium text-gray-800" data-id="element-6023">
                    {question.skill || '—'}
                  </span>
                </div>
                <div data-id="element-6024">
                  <span className="block text-xs text-gray-500 mb-1" data-id="element-6025">
                    SubSkill
                  </span>
                  <span className="font-medium text-gray-800" data-id="element-6026">
                    {question.subSkill || '—'}
                  </span>
                </div>
                <div data-id="element-6027">
                  <span className="block text-xs text-gray-500 mb-1" data-id="element-6028">
                    Level Code
                  </span>
                  <span className="font-medium text-gray-800" data-id="element-6029">
                    {question.levelCode || '—'}
                  </span>
                </div>
                <div data-id="element-6030">
                  <span className="block text-xs text-gray-500 mb-1" data-id="element-6031">
                    Groep / Tier
                  </span>
                  <div className="flex items-center gap-2" data-id="element-6032">
                    <span className="font-medium text-gray-800" data-id="element-6033">
                      Groep {question.group || '?'}
                    </span>
                    <Badge variant={getTierColor(question.tier)} size="sm" data-id="element-6034">
                      Tier {question.tier || '?'}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Pedagogy Section */}
            {(question.pedagogy?.dok || question.pedagogy?.misconceptionTags?.length) && <div className="p-4 sm:p-5 border-b border-gray-100 bg-slate-50/50" data-id="element-6035">
                <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-6036">
                  <BookOpen className="w-4 h-4 text-indigo-400" data-id="element-6037" />
                  Pedagogisch Contract
                </h4>
                <div className="flex flex-wrap gap-4" data-id="element-6038">
                  {question.pedagogy?.dok && <div data-id="element-6039">
                      <span className="text-xs text-gray-500 block mb-1.5" data-id="element-6040">
                        DOK Niveau
                      </span>
                      <Badge variant="neutral" className="bg-indigo-50 text-indigo-700 border-indigo-200" data-id="element-6041">
                        DOK {question.pedagogy.dok}
                      </Badge>
                    </div>}

                  {question.pedagogy?.misconceptionTags && question.pedagogy.misconceptionTags.length > 0 && <div data-id="element-6042">
                        <span className="text-xs text-gray-500 block mb-1.5 flex items-center gap-1" data-id="element-6043">
                          <Tag className="w-3 h-3" data-id="element-6044" /> Misconcepties
                        </span>
                        <div className="flex flex-wrap gap-1.5" data-id="element-6045">
                          {question.pedagogy.misconceptionTags.map(tag => <Badge key={tag.id} variant="warning" size="sm" data-id="element-6046">
                              {tag.label || tag.id}
                            </Badge>)}
                        </div>
                      </div>}
                </div>
              </div>}

            {/* Visual Aids Section */}
            {visualCount > 0 && <div className="p-4 sm:p-5 border-b border-gray-100" data-id="element-6047">
                <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-6048">
                  <Image className="w-4 h-4 text-purple-400" data-id="element-6049" />
                  Visuals ({visualCount})
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6050">
                  {question.stimulusAids && question.stimulusAids.length > 0 && <div className="space-y-3" data-id="element-6051">
                        <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-6052">
                          Stimulus Aids
                        </h5>
                        {question.stimulusAids.map((aid, idx) => <div key={`stim-${idx}`} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden" data-id="element-6053">
                            <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 flex justify-between items-center" data-id="element-6054">
                              <code className="text-xs font-mono font-bold text-purple-700" data-id="element-6055">
                                {aid.type}
                              </code>
                              <span className="text-[10px] text-gray-500 bg-white px-1.5 py-0.5 rounded border border-gray-200" data-id="element-6056">
                                {aid.showWhen || 'always'}
                              </span>
                            </div>
                            <div className="p-4 flex justify-center items-center min-h-[120px] bg-white" data-id="element-6057">
                              <div className="transform scale-75 origin-center w-full max-w-sm" data-id="element-6058">
                                <VisualAidFactory visualAid={aid} mode="stimulus" data-id="element-6059" />
                              </div>
                            </div>
                          </div>)}
                      </div>}

                  {question.supportAids && question.supportAids.length > 0 && <div className="space-y-3" data-id="element-6060">
                      <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-6061">
                        Support Aids
                      </h5>
                      {question.supportAids.map((aid, idx) => <div key={`supp-${idx}`} className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden" data-id="element-6062">
                          <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 flex justify-between items-center" data-id="element-6063">
                            <code className="text-xs font-mono font-bold text-amber-700" data-id="element-6064">
                              {aid.type}
                            </code>
                            <span className="text-[10px] text-gray-500 bg-white px-1.5 py-0.5 rounded border border-gray-200" data-id="element-6065">
                              {aid.showWhen || 'always'}
                            </span>
                          </div>
                          <div className="p-4 flex justify-center items-center min-h-[120px] bg-white" data-id="element-6066">
                            <div className="transform scale-75 origin-center w-full max-w-sm" data-id="element-6067">
                              <VisualAidFactory visualAid={aid} mode="support" data-id="element-6068" />
                            </div>
                          </div>
                        </div>)}
                    </div>}
                </div>
              </div>}

            {/* Feedback Completeness */}
            <div className="p-4 sm:p-5 bg-gray-50/50" data-id="element-6069">
              <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5" data-id="element-6070">
                <MessageSquare className="w-3.5 h-3.5" data-id="element-6071" />
                Feedback Compleetheid
              </h5>
              <div className="flex flex-wrap gap-2" data-id="element-6072">
                <Badge variant={question.feedback?.explanationText ? 'success' : 'danger'} size="sm" data-id="element-6073">
                  {question.feedback?.explanationText ? <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-6074" /> : <XCircle className="w-3 h-3 mr-1 inline" data-id="element-6075" />}
                  Uitleg tekst
                </Badge>
                <Badge variant={question.feedback?.solutionSteps?.length ? 'success' : 'warning'} size="sm" data-id="element-6076">
                  {question.feedback?.solutionSteps?.length ? <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-6077" /> : <XCircle className="w-3 h-3 mr-1 inline" data-id="element-6078" />}
                  Stappenplan
                </Badge>
                <Badge variant={question.feedback?.distractorExplanations && Object.keys(question.feedback.distractorExplanations).length > 0 ? 'success' : 'warning'} size="sm" data-id="element-6079">
                  {question.feedback?.distractorExplanations && Object.keys(question.feedback.distractorExplanations).length > 0 ? <CheckCircle2 className="w-3 h-3 mr-1 inline" data-id="element-6080" /> : <XCircle className="w-3 h-3 mr-1 inline" data-id="element-6081" />}
                  Fout-specifieke feedback
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}